<!--
 * @Description: 选择开票数据 弹窗（票税修改 v2 可复用组件）
 *               globalPreSelectedIds: 跨组全局已选ID → 传 API 排除
 *               currentPreSelectedIds: 本组已选ID → 预勾选
 * @Date: 2026-06-03
-->
<template>
  <BaseDialog
    width="1200px"
    :title="type === 1 ? '选择开票数据' : '数据明细'"
    :model-value="true"
    :close-on-press-escape="true"
    :close-on-click-modal="false"
    @close="onCancel"
  >
    <BaseTextContent>
      <XrkTable
        ref="xrkTableRef"
        :requestNow="true"
        :getListAjax="getListAjax"
        :searchConfig="searchConfig"
        :tableConfig="tableConfig"
      />
    </BaseTextContent>
    <template #footer>
      <template v-if="isAdd">
        <BaseButton type="default" @click="onCancel">取消</BaseButton>
        <BaseButton type="primary" @click="onConfirm">
          确认
          <template v-if="selectionCount">
            （已选 {{ selectionCount }}）
          </template>
        </BaseButton>
      </template>
      <BaseButton v-else type="default" @click="onCancel">关闭</BaseButton>
    </template>
  </BaseDialog>
</template>

<script lang="ts" setup>
import { ref, computed, nextTick, watch } from 'vue';
import {
  BaseDialog,
  BaseTextContent,
  BaseButton,
  XrkTable,
  XrkTableProps,
  useTableColumn,
  useInputColumn,
  useDatePickerColumn,
  useSelectColumn,
  XrkMessage
} from 'xrk-components';
import { formatter } from '@/utils';
import {
  individualNotInvoicedPageList,
  queryInvoiceOrder
} from '@V/invoice/src/data-source/apis';
import { SETTLEMENT_STATUS_OPTIONS } from '@V/invoice/src/constants/index';

const props = withDefaults(
  defineProps<{
    /** 跨组全局已选 settlementUserTaskId（传入 API 排除 + 预勾选） */
    globalPreSelectedIds?: number[];
    /** 本组已选 settlementUserTaskId（仅预勾选） */
    currentPreSelectedIds?: number[];
    /** 个体户ID（由选择个体户名称后获取，传给 API 过滤数据） */
    individualId?: number; // 新增必传
    remove?: () => void;
    invoiceApplyId?: number; // 查看必传
    invoiceBusinessType?: number; // 查看必传
    type: 1 | 2; // 1选择数据 2查看
  }>(),
  {
    globalPreSelectedIds: () => [],
    currentPreSelectedIds: () => []
  }
);

const emits = defineEmits<{
  (event: 'confirm', value: any[]): void;
}>();

const isAdd = props.type === 1;
const ajaxFn = isAdd ? individualNotInvoicedPageList : queryInvoiceOrder;

const xrkTableRef = ref();
const ajaxDone = ref(false);

const selectionCount = computed(() => {
  return xrkTableRef.value?.getSelectionRows?.()?.length || 0;
});

/** 传给 API 排除的 ID 集合：全局已选 减去 本组已选（本组自己的数据仍需出现在列表中） */
const excludeIds = computed(() => {
  const set = new Set(props.globalPreSelectedIds);
  props.currentPreSelectedIds.forEach(id => set.delete(id));
  return [...set];
});

const preSelectRows = () => {
  nextTick(() => {
    if (!xrkTableRef.value) return;
    const table = xrkTableRef.value;
    const data = table.getTableData?.()?.value || table.getTableData?.() || [];
    const rows = Array.isArray(data) ? data : [];
    rows.forEach(row => {
      if (props.currentPreSelectedIds.includes(row.settlementUserTaskId)) {
        table.addSelectionRow?.(row.settlementUserTaskId, row);
      }
    });
  });
};

const getListAjax = async (params: object) => {
  const resp = await ajaxFn({
    ...params,
    ...(props.individualId && {
      individualId: props.individualId
    }),
    ...(excludeIds.value.length && {
      settlementUserTaskIds: excludeIds.value
    }),
    ...(props.invoiceApplyId && {
      invoiceApplyId: props.invoiceApplyId
    }),
    ...(props.invoiceBusinessType && {
      invoiceBusinessType: props.invoiceBusinessType
    })
  } as any);
  ajaxDone.value = true;
  return resp;
};

watch(
  () => ajaxDone.value,
  v => {
    if (v) {
      nextTick(() => preSelectRows());
    }
  }
);

const searchConfig = ref<XrkTableProps['searchConfig']>({
  searchColumns: [
    useInputColumn({
      prop: 'settlementNo',
      label: '结算单号',
      config: { placeholder: '请输入结算单号', clearable: true }
    }),
    useInputColumn({
      prop: 'projectName',
      label: '项目名称',
      config: { placeholder: '请输入项目名称', clearable: true }
    }),
    useInputColumn({
      prop: 'industryName',
      label: '工业名称',
      config: { placeholder: '请输入工业名称', clearable: true }
    }),
    useDatePickerColumn({
      prop: 'settlementConfirmTime',
      label: '结算单确认时间',
      config: {
        type: 'daterange',
        startPlaceholder: '开始日期',
        endPlaceholder: '结束日期',
        clearable: true
      },
      listener: {
        onChange: (value, searchObj) => {
          searchObj.settlementConfirmTimeStart = value ? value[0] : '';
          searchObj.settlementConfirmTimeEnd = value ? value[1] : '';
        }
      }
    }),
    useSelectColumn({
      prop: 'settlementStatus',
      label: '结算单状态',
      config: {
        clearable: true,
        placeholder: '请选择',
        options: SETTLEMENT_STATUS_OPTIONS
      }
    })
  ],
  searchBtn: { show: true, text: '搜索' },
  resetBtn: { show: true, text: '重置' }
});

const tableConfig = computed<XrkTableProps['tableConfig']>(() => ({
  tableColumns: [
    isAdd &&
      useTableColumn({
        type: '_selection',
        prop: 'settlementUserTaskId',
        align: 'center',
        fixed: 'left',
        width: 110
      }),
    useTableColumn({
      type: 'index',
      label: '序号',
      width: '60px',
      fixed: 'left'
    }),
    useTableColumn({ label: '结算单号', prop: 'settlementNo', width: '180px' }),
    useTableColumn({
      label: '项目名称',
      prop: 'projectName',
      minWidth: '140px'
    }),
    useTableColumn({ label: '工业名称', prop: 'industryName', width: '160px' }),
    useTableColumn({
      label: '服务商名称',
      prop: 'serviceProviderName',
      width: '160px'
    }),
    useTableColumn({
      label: '个体户名称',
      prop: 'individualName',
      width: '150px'
    }),
    useTableColumn({ label: '经营者', prop: 'operator', width: '100px' }),
    useTableColumn({
      label: '手机号',
      prop: 'phone',
      width: '130px',
      formatter: ({ cellValue }) => formatter.maskPhone(cellValue)
    }),
    useTableColumn({
      label: '终端结算金额(元)',
      prop: 'terminalSettlementAmount',
      width: '160px',
      formatter: ({ cellValue }) => formatter.fen2yuan(cellValue)
    }),
    useTableColumn({
      label: '结算单确认时间',
      prop: 'settlementConfirmTime',
      minWidth: '170px',
      formatter: ({ cellValue }) => formatter.date(cellValue)
    }),
    useTableColumn({
      label: '结算单状态',
      prop: 'settlementStatus',
      width: '120px',
      formatter: ({ cellValue }) =>
        formatter.defaultFormat(
          cellValue,
          () =>
            SETTLEMENT_STATUS_OPTIONS.find(o => o.value === cellValue)?.label ||
            '-'
        )
    })
  ].filter(Boolean)
}));

const onConfirm = () => {
  const rows = xrkTableRef.value?.getSelectionRows?.() || [];
  if (!rows.length) {
    XrkMessage.warning('请选择开票数据');
    return;
  }
  emits('confirm', rows);
  props.remove?.();
};

const onCancel = () => {
  props.remove?.();
};
</script>
